import React from 'react';
import {
  Box,
  Heading,
  Text,
  Badge,
  Flex,
  Code,
  Divider,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from '@chakra-ui/react';

const methodColors = {
  GET: 'green',
  POST: 'blue',
  PUT: 'orange',
  PATCH: 'yellow',
  DELETE: 'red',
  WS: 'purple',
};

const EndpointCard = ({ endpoint }) => (
  <Box
    p={5}
    bg="white"
    borderRadius="xl"
    boxShadow="sm"
    borderWidth="1px"
    borderColor="gray.100"
    transition="all 0.2s"
    _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
  >
    <Flex align="center" mb={3}>
      <Badge
        colorScheme={methodColors[endpoint.method] || 'gray'}
        px={3}
        py={1}
        borderRadius="full"
        fontWeight="bold"
        fontSize="xs"
      >
        {endpoint.method}
      </Badge>
      <Text ml={3} fontWeight="semibold" fontSize="md" color="gray.700">
        {endpoint.title}
      </Text>
    </Flex>
    <Code
      display="block"
      p={3}
      borderRadius="md"
      bg="gray.50"
      fontSize="sm"
      color="purple.600"
      fontWeight="medium"
    >
      {endpoint.path}
    </Code>
    <Text mt={3} fontSize="sm" color="gray.500">
      {endpoint.description}
    </Text>
    {endpoint.auth && (
      <Flex mt={2} align="center" gap={2}>
        <Badge colorScheme="orange" variant="subtle" fontSize="xs">Auth required</Badge>
        <Text fontSize="xs" color="gray.400">{endpoint.auth}</Text>
      </Flex>
    )}

    {(endpoint.request || endpoint.response) && (
      <Accordion allowToggle mt={4}>
        {endpoint.request && (
          <AccordionItem border="none">
            <AccordionButton px={3} py={2} bg="blue.50" borderRadius="md" _hover={{ bg: 'blue.100' }}>
              <Box flex="1" textAlign="left">
                <Badge colorScheme="blue" variant="subtle" fontSize="xs">Request Body</Badge>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={2} pt={2}>
              <Code
                display="block" p={3} borderRadius="md" bg="gray.800"
                color="green.300" fontSize="xs" whiteSpace="pre" overflowX="auto"
              >
                {endpoint.request}
              </Code>
            </AccordionPanel>
          </AccordionItem>
        )}
        {endpoint.response && (
          <AccordionItem border="none" mt={endpoint.request ? 2 : 0}>
            <AccordionButton px={3} py={2} bg="green.50" borderRadius="md" _hover={{ bg: 'green.100' }}>
              <Box flex="1" textAlign="left">
                <Badge colorScheme="green" variant="subtle" fontSize="xs">Response</Badge>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={2} pt={2}>
              <Code
                display="block" p={3} borderRadius="md" bg="gray.800"
                color="green.300" fontSize="xs" whiteSpace="pre" overflowX="auto"
              >
                {endpoint.response}
              </Code>
            </AccordionPanel>
          </AccordionItem>
        )}
      </Accordion>
    )}
  </Box>
);

const Section = ({ title, count, children }) => (
  <Box mb={8}>
    <Flex align="center" mb={4}>
      <Heading size="md" color="gray.700">{title}</Heading>
      <Badge ml={3} colorScheme="teal" borderRadius="full" px={3}>{count} endpoint{count !== 1 ? 's' : ''}</Badge>
    </Flex>
    <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
      {children}
    </SimpleGrid>
  </Box>
);

const ApiDocs = () => {
  const authEndpoints = [
    {
      title: 'Login',
      method: 'POST',
      path: '/api/v1/auth/login/',
      description: 'Authenticate with username and password. Returns a DRF token for use in subsequent requests.',
      request: `{
  "username": "admin",
  "password": "changeme"
}`,
      response: `{
  "token": "9944b09199c62bcf9418ad846dd0e4bbdfc6ee4b"
}`,
    },
    {
      title: 'Ping',
      method: 'GET',
      path: '/api/v1/ping',
      description: 'Public health check. Returns service name and status. No authentication required.',
      response: `{
  "status": "ok",
  "service": "meet"
}`,
    },
    {
      title: 'Health Check',
      method: 'GET',
      path: '/api/v1/health',
      description: 'Detailed health check including database connectivity.',
      auth: 'Authorization: Token <token>',
      response: `{
  "status": "ok",
  "database": "connected",
  "service": "meet"
}`,
    },
  ];

  const tokenEndpoints = [
    {
      title: 'Issue Join Token',
      method: 'POST',
      path: '/api/v1/join-token',
      description: 'Issue a short-lived JWT (15 min by default) allowing a user to join a room. Permissions are derived from the room configuration and the requested role.',
      auth: 'Authorization: Token <token>  or  X-API-Key: <key>',
      request: `{
  "room_id": "abc12345",
  "user_id": "user_xyz",
  "role": "host",
  "display_name": "Jane Smith"
}`,
      response: `{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...",
  "room_id": "abc12345",
  "expires_at": "2026-02-14T12:15:00+00:00"
}`,
    },
    {
      title: 'Verify Join Token',
      method: 'POST',
      path: '/api/v1/verify-token',
      description: 'Verify a JWT join token and return its decoded payload. Used internally by the signaling service to authenticate WebSocket connections.',
      request: `{
  "token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
}`,
      response: `{
  "valid": true,
  "payload": {
    "room_id": "abc12345",
    "user_id": "user_xyz",
    "role": "host",
    "display_name": "Jane Smith",
    "permissions": {
      "publish": true,
      "screenshare": true,
      "chat": true
    },
    "exp": 1739534100,
    "iat": 1739533200
  }
}`,
    },
    {
      title: 'Public Room Info',
      method: 'GET',
      path: '/api/v1/rooms/:room_id/info',
      description: 'Retrieve basic public information about a room. Used by the join page to display room details before a user enters. No authentication required.',
      response: `{
  "room_id": "abc12345",
  "name": "Team Standup",
  "room_type": "meeting",
  "max_participants": 100,
  "chat_enabled": true,
  "screenshare_enabled": true,
  "participant_count": 3
}`,
    },
  ];

  const orgEndpoints = [
    {
      title: 'List organizations',
      method: 'GET',
      path: '/api/v1/organizations/',
      description: 'List all organizations with their room counts.',
      auth: 'Authorization: Token <token>',
      response: `[
  {
    "id": 1,
    "name": "Acme Corp",
    "slug": "acme-corp",
    "is_active": true,
    "room_count": 5,
    "created_at": "2026-01-01T00:00:00Z",
    "updated_at": "2026-01-01T00:00:00Z"
  }
]`,
    },
    {
      title: 'Create organization',
      method: 'POST',
      path: '/api/v1/organizations/',
      description: 'Create a new organization.',
      auth: 'Authorization: Token <token>',
      request: `{
  "name": "Acme Corp",
  "slug": "acme-corp"
}`,
      response: `{
  "id": 1,
  "name": "Acme Corp",
  "slug": "acme-corp",
  "is_active": true,
  "room_count": 0,
  "created_at": "2026-02-14T10:00:00Z",
  "updated_at": "2026-02-14T10:00:00Z"
}`,
    },
    {
      title: 'Update organization',
      method: 'PUT',
      path: '/api/v1/organizations/:id/',
      description: 'Update an existing organization.',
      auth: 'Authorization: Token <token>',
      request: `{
  "name": "Acme Corporation",
  "slug": "acme-corporation",
  "is_active": true
}`,
      response: `{
  "id": 1,
  "name": "Acme Corporation",
  "slug": "acme-corporation",
  "is_active": true,
  "room_count": 5,
  "updated_at": "2026-02-14T11:00:00Z"
}`,
    },
    {
      title: 'Delete organization',
      method: 'DELETE',
      path: '/api/v1/organizations/:id/',
      description: 'Delete an organization. Associated rooms and API keys are also deleted.',
      auth: 'Authorization: Token <token>',
      response: `204 No Content`,
    },
  ];

  const roomEndpoints = [
    {
      title: 'List rooms',
      method: 'GET',
      path: '/api/v1/rooms/',
      description: 'List all rooms with live status and current participant count.',
      auth: 'Authorization: Token <token>',
      response: `[
  {
    "id": 1,
    "room_id": "abc12345",
    "name": "Team Standup",
    "room_type": "meeting",
    "organization": 1,
    "organization_name": "Acme Corp",
    "max_participants": 100,
    "chat_enabled": true,
    "screenshare_enabled": true,
    "is_active": true,
    "is_live": true,
    "participant_count": 4,
    "created_at": "2026-02-14T09:00:00Z",
    "updated_at": "2026-02-14T09:00:00Z"
  }
]`,
    },
    {
      title: 'Create room',
      method: 'POST',
      path: '/api/v1/rooms/',
      description: 'Create a new room. The room_id is auto-generated as a short unique identifier.',
      auth: 'Authorization: Token <token>',
      request: `{
  "name": "Product Demo",
  "room_type": "webinar",
  "organization": 1,
  "max_participants": 500,
  "chat_enabled": true,
  "screenshare_enabled": true
}`,
      response: `{
  "id": 2,
  "room_id": "xyz98765",
  "name": "Product Demo",
  "room_type": "webinar",
  "is_active": true,
  "is_live": false,
  "participant_count": 0
}`,
    },
    {
      title: 'List active rooms',
      method: 'GET',
      path: '/api/v1/rooms/active/',
      description: 'List only rooms that currently have an active session (i.e. at least one connected participant).',
      auth: 'Authorization: Token <token>',
      response: `[
  {
    "id": 1,
    "room_id": "abc12345",
    "name": "Team Standup",
    "is_live": true,
    "participant_count": 4
  }
]`,
    },
    {
      title: 'End session',
      method: 'POST',
      path: '/api/v1/rooms/:id/end_session/',
      description: 'Administratively end the active session for a room. Marks the session as ended; participants will be disconnected when they next interact.',
      auth: 'Authorization: Token <token>',
      response: `{
  "status": "session ended"
}`,
    },
    {
      title: 'Update room',
      method: 'PUT',
      path: '/api/v1/rooms/:id/',
      description: 'Update room configuration. Changes take effect for the next session.',
      auth: 'Authorization: Token <token>',
      request: `{
  "name": "Updated Room Name",
  "max_participants": 50,
  "chat_enabled": false
}`,
      response: `{
  "id": 1,
  "room_id": "abc12345",
  "name": "Updated Room Name",
  "max_participants": 50,
  "chat_enabled": false,
  "screenshare_enabled": true
}`,
    },
    {
      title: 'Delete room',
      method: 'DELETE',
      path: '/api/v1/rooms/:id/',
      description: 'Delete a room and all associated sessions.',
      auth: 'Authorization: Token <token>',
      response: `204 No Content`,
    },
  ];

  const sessionEndpoints = [
    {
      title: 'List sessions',
      method: 'GET',
      path: '/api/v1/sessions/',
      description: 'List meeting sessions. Filter by room or active status using query parameters: ?room_id=abc12345 or ?active=true',
      auth: 'Authorization: Token <token>',
      response: `[
  {
    "id": 1,
    "session_id": "550e8400-e29b-41d4-a716-446655440000",
    "room": 1,
    "started_at": "2026-02-14T09:30:00Z",
    "ended_at": null,
    "participant_count": 4,
    "participants": [
      {
        "id": 1,
        "user_id": "user_abc",
        "display_name": "Jane Smith",
        "role": "host",
        "joined_at": "2026-02-14T09:30:05Z",
        "left_at": null
      }
    ]
  }
]`,
    },
    {
      title: 'Get session',
      method: 'GET',
      path: '/api/v1/sessions/:id/',
      description: 'Retrieve a specific session with full participant list.',
      auth: 'Authorization: Token <token>',
      response: `{
  "id": 1,
  "session_id": "550e8400-e29b-41d4-a716-446655440000",
  "room": 1,
  "started_at": "2026-02-14T09:30:00Z",
  "ended_at": null,
  "participant_count": 4,
  "participants": [...]
}`,
    },
  ];

  const apiKeyEndpoints = [
    {
      title: 'List API keys',
      method: 'GET',
      path: '/api/v1/api-keys/',
      description: 'List all API keys with their organization associations.',
      auth: 'Authorization: Token <token>',
      response: `[
  {
    "id": 1,
    "name": "Production Key",
    "key": "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
    "organization": 1,
    "organization_name": "Acme Corp",
    "is_active": true,
    "created_at": "2026-01-01T00:00:00Z"
  }
]`,
    },
    {
      title: 'Create API key',
      method: 'POST',
      path: '/api/v1/api-keys/',
      description: 'Create a new API key. The key value is auto-generated as a UUID.',
      auth: 'Authorization: Token <token>',
      request: `{
  "name": "Mobile App Key",
  "organization": 1
}`,
      response: `{
  "id": 2,
  "name": "Mobile App Key",
  "key": "f47ac10b-58cc-4372-a567-0e02b2c3d479",
  "organization": 1,
  "organization_name": "Acme Corp",
  "is_active": true,
  "created_at": "2026-02-14T10:00:00Z"
}`,
    },
    {
      title: 'Revoke API key',
      method: 'POST',
      path: '/api/v1/api-keys/:id/revoke/',
      description: 'Revoke an API key. The key remains in the database but is marked inactive and will be rejected on use.',
      auth: 'Authorization: Token <token>',
      response: `{
  "status": "revoked"
}`,
    },
    {
      title: 'Delete API key',
      method: 'DELETE',
      path: '/api/v1/api-keys/:id/',
      description: 'Permanently delete an API key record.',
      auth: 'Authorization: Token <token>',
      response: `204 No Content`,
    },
  ];

  const auditEndpoints = [
    {
      title: 'List audit logs',
      method: 'GET',
      path: '/api/v1/audit-logs/',
      description: 'Retrieve the audit log. Ordered by most recent first. Records are created automatically for room creation/deletion, session start/end, API key creation/revocation, and participant join/leave events.',
      auth: 'Authorization: Token <token>',
      response: `[
  {
    "id": 1,
    "action": "room_created",
    "actor": "admin",
    "resource_type": "room",
    "resource_id": "1",
    "details": {
      "name": "Team Standup",
      "type": "meeting"
    },
    "created_at": "2026-02-14T09:00:00Z"
  }
]`,
    },
  ];

  const wsEvents = [
    {
      title: 'Connect',
      method: 'WS',
      path: 'wss://meet.selwynevents.com/ws?token=<jwt>',
      description: 'Open a WebSocket connection. The JWT join token must be passed as a query parameter. On success the server sends a "connected" message.',
      response: `// Server → Client on connect
{
  "type": "connected",
  "user_id": "user_xyz",
  "room_id": "abc12345",
  "permissions": {
    "publish": true,
    "screenshare": true,
    "chat": true
  }
}`,
    },
    {
      title: 'SDP Offer / Answer',
      method: 'WS',
      path: 'wss://meet.selwynevents.com/ws',
      description: 'Exchange WebRTC SDP offers and answers for peer-to-peer connection setup. Addressed to a specific peer via to_user.',
      request: `// Client → Server (offer)
{
  "type": "offer",
  "to_user": "user_abc",
  "sdp": "v=0\\r\\no=- ..."
}

// Client → Server (answer)
{
  "type": "answer",
  "to_user": "user_abc",
  "sdp": "v=0\\r\\no=- ..."
}`,
      response: `// Server → Client (forwarded offer/answer)
{
  "type": "offer",
  "from_user": "user_abc",
  "sdp": "v=0\\r\\no=- ..."
}`,
    },
    {
      title: 'ICE Candidates',
      method: 'WS',
      path: 'wss://meet.selwynevents.com/ws',
      description: 'Relay ICE candidates between peers for NAT traversal.',
      request: `{
  "type": "ice_candidate",
  "to_user": "user_abc",
  "candidate": {
    "candidate": "candidate:1 1 UDP ...",
    "sdpMid": "0",
    "sdpMLineIndex": 0
  }
}`,
      response: `{
  "type": "ice_candidate",
  "from_user": "user_abc",
  "candidate": { ... }
}`,
    },
    {
      title: 'Mute State',
      method: 'WS',
      path: 'wss://meet.selwynevents.com/ws',
      description: 'Broadcast audio/video mute state to all participants in the room.',
      request: `{
  "type": "mute_state",
  "audio_muted": true,
  "video_muted": false
}`,
      response: `// Broadcast to all participants
{
  "type": "mute_state",
  "user_id": "user_xyz",
  "audio_muted": true,
  "video_muted": false
}`,
    },
    {
      title: 'Chat Messages',
      method: 'WS',
      path: 'wss://meet.selwynevents.com/ws',
      description: 'Send a text chat message to all participants. For files <64 KB use WebRTC DataChannel; for larger files upload to object storage and share the URL as a text message.',
      request: `{
  "type": "chat_message",
  "message": "Hello everyone!",
  "message_type": "text"
}`,
      response: `// Broadcast to all participants
{
  "type": "chat_message",
  "user_id": "user_xyz",
  "display_name": "Jane Smith",
  "message": "Hello everyone!",
  "message_type": "text"
}`,
    },
    {
      title: 'Screen Share',
      method: 'WS',
      path: 'wss://meet.selwynevents.com/ws',
      description: 'Notify participants that screen sharing has started or stopped. Only users with screenshare permission can initiate. Only one active screenshare per room; the host can override.',
      request: `// Start
{ "type": "screenshare_started" }

// Stop
{ "type": "screenshare_stopped" }`,
      response: `// Broadcast to all participants
{
  "type": "screenshare_started",
  "user_id": "user_xyz",
  "display_name": "Jane Smith"
}

{
  "type": "screenshare_stopped",
  "user_id": "user_xyz"
}`,
    },
    {
      title: 'Participant Events',
      method: 'WS',
      path: 'wss://meet.selwynevents.com/ws',
      description: 'Automatically broadcast when participants join or leave the room. No client action required.',
      response: `// Join
{
  "type": "participant_joined",
  "user_id": "user_new",
  "display_name": "Bob Jones",
  "role": "attendee"
}

// Leave
{
  "type": "participant_left",
  "user_id": "user_new",
  "display_name": "Bob Jones"
}`,
    },
  ];

  return (
    <Box>
      <Box mb={8}>
        <Heading size="lg" color="gray.800" mb={2}>API Documentation</Heading>
        <Text color="gray.500">
          REST endpoints for managing rooms, organisations, and API keys. The WebSocket signaling
          protocol is documented at the bottom.
        </Text>
      </Box>

      <Box mb={6} p={4} bg="teal.50" borderRadius="xl" borderLeft="4px solid" borderColor="teal.400">
        <Heading size="sm" color="teal.700" mb={2}>Authentication</Heading>
        <Text fontSize="sm" color="teal.800" mb={1}>
          Admin endpoints accept a DRF token in the <Code colorScheme="teal">Authorization</Code> header:
        </Text>
        <Code display="block" p={2} bg="teal.100" borderRadius="md" fontSize="xs" color="teal.900">
          Authorization: Token 9944b09199c62bcf9418ad846dd0e4bbdfc6ee4b
        </Code>
        <Text fontSize="sm" color="teal.800" mt={2} mb={1}>
          Service-to-service calls may use an API key instead:
        </Text>
        <Code display="block" p={2} bg="teal.100" borderRadius="md" fontSize="xs" color="teal.900">
          X-API-Key: f47ac10b-58cc-4372-a567-0e02b2c3d479
        </Code>
        <Text fontSize="sm" color="teal.800" mt={2}>
          Room participants authenticate via a short-lived JWT issued by <Code colorScheme="teal">/api/v1/join-token</Code>.
        </Text>
      </Box>

      <Section title="Authentication & Health" count={authEndpoints.length}>
        {authEndpoints.map((e, i) => <EndpointCard key={i} endpoint={e} />)}
      </Section>

      <Divider my={8} />

      <Section title="Join Tokens" count={tokenEndpoints.length}>
        {tokenEndpoints.map((e, i) => <EndpointCard key={i} endpoint={e} />)}
      </Section>

      <Divider my={8} />

      <Section title="Rooms" count={roomEndpoints.length}>
        {roomEndpoints.map((e, i) => <EndpointCard key={i} endpoint={e} />)}
      </Section>

      <Divider my={8} />

      <Section title="Sessions" count={sessionEndpoints.length}>
        {sessionEndpoints.map((e, i) => <EndpointCard key={i} endpoint={e} />)}
      </Section>

      <Divider my={8} />

      <Section title="Organisations" count={orgEndpoints.length}>
        {orgEndpoints.map((e, i) => <EndpointCard key={i} endpoint={e} />)}
      </Section>

      <Divider my={8} />

      <Section title="API Keys" count={apiKeyEndpoints.length}>
        {apiKeyEndpoints.map((e, i) => <EndpointCard key={i} endpoint={e} />)}
      </Section>

      <Divider my={8} />

      <Section title="Audit Log" count={auditEndpoints.length}>
        {auditEndpoints.map((e, i) => <EndpointCard key={i} endpoint={e} />)}
      </Section>

      <Divider my={8} />

      <Section title="WebSocket Signaling" count={wsEvents.length}>
        {wsEvents.map((e, i) => <EndpointCard key={i} endpoint={e} />)}
      </Section>
    </Box>
  );
};

export default ApiDocs;
